// frontend/src/admin/pages/AdminConsentPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import { listConsentLog } from "../api";

const DOC_LABELS = {
  tos: "Terms of Service",
  privacy_policy: "Privacy Policy",
  dpa: "Data Processing Addendum",
  cookie_policy: "Cookie Policy",
};
const DOC_FILTERS = ["", "tos", "privacy_policy", "cookie_policy", "dpa"];

function fmt(iso) {
  return iso ? new Date(iso).toLocaleString() : "—";
}

/**
 * AdminConsentPage — org-wide consent ledger (P20). A searchable, filterable log
 * of every recorded policy/cookie acceptance, for when proof of consent is
 * required. Append-only data; this view is read-only.
 */
function AdminConsentPage() {
  const [data, setData] = useState({ items: [], page: 1, per_page: 50, total: 0 });
  const [q, setQ] = useState("");
  const [docType, setDocType] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 50 };
      if (q.trim()) params.q = q.trim();
      if (docType) params.document_type = docType;
      setData(await listConsentLog(params));
    } catch {
      toast.error("Failed to load the consent log.");
    } finally {
      setLoading(false);
    }
  }, [page, q, docType]);

  useEffect(() => {
    load();
  }, [load]);

  const pages = Math.max(1, Math.ceil(data.total / data.per_page));

  return (
    <div className="space-y-4 max-w-5xl">
      <div>
        <h1 className="text-xl font-bold text-slate-800">Consent log</h1>
        <p className="mt-1 text-sm text-slate-500">
          Every recorded acceptance of a legal document or cookie choice, with
          version, time, and anonymised IP — the proof-of-consent ledger.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
          placeholder="Search by email…"
          className="rounded-md border-slate-300 text-sm px-3 py-1.5"
        />
        <select
          value={docType}
          onChange={(e) => {
            setPage(1);
            setDocType(e.target.value);
          }}
          className="rounded-md border-slate-300 text-sm"
        >
          {DOC_FILTERS.map((d) => (
            <option key={d || "all"} value={d}>
              {d ? DOC_LABELS[d] : "All documents"}
            </option>
          ))}
        </select>
        <span className="text-xs text-slate-400">{data.total} record(s)</span>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-2 font-medium">User</th>
              <th className="px-4 py-2 font-medium">Document</th>
              <th className="px-4 py-2 font-medium">Version</th>
              <th className="px-4 py-2 font-medium">Accepted</th>
              <th className="px-4 py-2 font-medium">IP (anon.)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                  Loading…
                </td>
              </tr>
            ) : data.items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                  No consent records.
                </td>
              </tr>
            ) : (
              data.items.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2 text-slate-700">
                    {r.email ||
                      (r.user_id ? `#${r.user_id}` : "Anonymous visitor")}
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {DOC_LABELS[r.document_type] || r.document_type}
                  </td>
                  <td className="px-4 py-2 text-slate-500 font-mono text-xs">
                    {r.document_version}
                  </td>
                  <td className="px-4 py-2 text-slate-500 text-xs whitespace-nowrap">
                    {fmt(r.accepted_at)}
                  </td>
                  <td className="px-4 py-2 text-slate-400 font-mono text-xs">
                    {r.ip_subnet || "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 rounded border border-slate-300 disabled:opacity-40"
          >
            Previous
          </button>
          <span>
            Page {data.page} of {pages}
          </span>
          <button
            type="button"
            disabled={page >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            className="px-3 py-1 rounded border border-slate-300 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminConsentPage;
